import { CompareBars, RankedBars, Stack } from '@integra/ui';
import type { ReportSnapshot } from '../api/types.js';

/**
 * The rendered report (§20.5) — the SAME view in-app and on the public portal, so
 * what a client sees is exactly what the operator sees. White-label header from
 * the org branding; measured-vs-estimated bars from the frozen snapshot (§2.7).
 */

function str(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

export function ReportView({ title, snapshot }: { title: string; snapshot: ReportSnapshot }) {
  const productName = str(snapshot.branding['productName']) ?? snapshot.organisationName;
  const logoUrl = str(snapshot.branding['logoUrl']);
  const accent = str(snapshot.branding['accentColour']) ?? str(snapshot.branding['primaryColour']);

  return (
    <Stack gap="lg">
      {/* The colour is workspace branding DATA, so it is painted with an SVG fill
          attribute (allowed) rather than an inline style (§21.4). */}
      <svg
        viewBox="0 0 100 1"
        preserveAspectRatio="none"
        className="h-1.5 w-full"
        aria-hidden="true"
      >
        <rect x={0} y={0} width={100} height={1} fill={accent ?? 'var(--integra-gold-500)'} />
      </svg>

      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {logoUrl !== null && (
            <img
              src={logoUrl}
              alt={productName}
              className="h-10 w-auto max-w-[160px] object-contain"
            />
          )}
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-ink-muted">
              {productName}
            </p>
            <h1 className="font-heading text-2xl text-navy-700">{title}</h1>
          </div>
        </div>
        <div className="text-right text-xs text-ink-muted">
          <p>
            {fmtDate(snapshot.period.from)} – {fmtDate(snapshot.period.to)}
          </p>
          <p>Generated {fmtDate(snapshot.generatedAt)}</p>
        </div>
      </header>

      <section>
        <h2 className="mb-3 font-heading text-base text-navy-700">Measured performance</h2>
        {snapshot.metrics.length === 0 ? (
          <p className="text-sm text-ink-muted">
            No measured or estimated figures for this period. Nothing is invented — an absence of
            data is stated, not drawn as a zero.
          </p>
        ) : (
          <CompareBars
            groups={snapshot.metrics.map((m) => ({
              label: m.metric.replace(/_/g, ' '),
              measured: m.measured,
              estimated: m.estimated,
            }))}
            format={(n) => n.toLocaleString()}
          />
        )}
      </section>

      {snapshot.topLinks.length > 0 && (
        <section>
          <h2 className="mb-3 font-heading text-base text-navy-700">Top tracked links</h2>
          <RankedBars
            items={snapshot.topLinks.map((l) => ({
              label: l.label,
              value: l.clicks,
              valueLabel: `${l.clicks.toLocaleString()} clicks`,
            }))}
          />
        </section>
      )}

      <p className="border-t border-edge pt-3 text-[11px] text-ink-muted">
        Measured figures come from each channel&rsquo;s own API; estimates are computed and shown
        apart, never merged. Powered by {productName}.
      </p>
    </Stack>
  );
}
